import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { TenantsAPI } from "@/lib/sprint2-api";
import { UsersAdminAPI, RoleTemplatesAPI } from "@/lib/sprint3-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Search, Power, PowerOff, KeyRound, ShieldCheck, Copy, Check } from "lucide-react";

const ALL_ROLES = [
  "super_admin", "inst_admin", "principal", "hod", "teacher",
  "accountant", "librarian", "transport_admin", "warden", "parent", "student",
];

export default function UsersAdmin() {
  const { user } = useAuth();
  const isSuper = user?.roles?.includes("super_admin");
  const queryClient = useQueryClient();

  const [tenantId, setTenantId] = useState(isSuper ? "" : user?.tenant_id || "");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [rolesUser, setRolesUser] = useState(null);
  const [resetResult, setResetResult] = useState(null);

  const tenantsQuery = useQuery({
    queryKey: ["tenants"], queryFn: () => TenantsAPI.list(), enabled: isSuper,
  });
  useEffect(() => {
    if (isSuper && !tenantId && tenantsQuery.data?.length) setTenantId(tenantsQuery.data[0].id);
  }, [isSuper, tenantId, tenantsQuery.data]);

  const tenantParam = isSuper && tenantId ? { tenant_id: tenantId } : undefined;
  const effectiveTenant = isSuper ? tenantId : user?.tenant_id;

  const usersQuery = useQuery({
    queryKey: ["admin-users", effectiveTenant, search, roleFilter],
    queryFn: () => UsersAdminAPI.list({
      ...(tenantParam || {}),
      ...(search ? { q: search } : {}),
      ...(roleFilter !== "all" ? { role: roleFilter } : {}),
    }),
    enabled: !!effectiveTenant,
  });

  const toggleActive = async (u) => {
    try {
      await UsersAdminAPI.updateActive(u.id, !u.is_active);
      toast.success(u.is_active ? "Deactivated" : "Reactivated");
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const issueReset = async (u) => {
    try {
      const out = await UsersAdminAPI.passwordReset(u.id);
      setResetResult({ email: u.email, ...out });
      toast.success("Reset token issued");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <AppShell>
      <div className="p-8" data-testid="users-admin-page">
        <div className="mb-8">
<h1 className="font-display font-extrabold tracking-tight text-4xl">Roles &amp; Permissions</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Every login account in your tenant — see roles, last-login, MFA
            status. Adjust role bindings or issue a password reset on behalf of a user.
            New modules added to the platform are automatically available here for permission assignment.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 mb-5 p-4 bg-card rounded-2xl shadow-sm">
          {isSuper && (
            <div className="space-y-1.5 min-w-[180px]">
              <Label className="text-[10px] font-semibold uppercase tracking-wider">Tenant</Label>
              <Select value={tenantId} onValueChange={setTenantId}>
                <SelectTrigger data-testid="users-scope-tenant"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {tenantsQuery.data?.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5 min-w-[160px]">
            <Label className="text-[10px] font-semibold uppercase tracking-wider">Role</Label>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger data-testid="users-role-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                {ALL_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 flex-1 min-w-[200px]">
            <Label className="text-[10px] font-semibold uppercase tracking-wider">Search</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" placeholder="Name or email…" data-testid="users-search" />
            </div>
          </div>
        </div>

        <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead>MFA</TableHead>
                <TableHead>Last login</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usersQuery.isLoading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</TableCell></TableRow>
              ) : !usersQuery.data?.length ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-sm text-muted-foreground">No users.</TableCell></TableRow>
              ) : usersQuery.data.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell>
                    <div className="font-medium">{u.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{u.email}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {u.roles.map((r) => <Badge key={r} variant="secondary" className="text-[10px] capitalize">{r.replace("_", " ")}</Badge>)}
                    </div>
                  </TableCell>
                  <TableCell>
                    {u.mfa_enabled
                      ? <Badge className="bg-primary/10 text-primary border-primary/20"><ShieldCheck className="h-3 w-3 mr-1" />ON</Badge>
                      : <Badge variant="outline" className="text-muted-foreground">OFF</Badge>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : "never"}
                  </TableCell>
                  <TableCell>
                    {u.is_active
                      ? <Badge className="bg-[hsl(var(--success))/0.1] text-[hsl(var(--success))] border-[hsl(var(--success))/0.2]">Active</Badge>
                      : <Badge variant="outline" className="text-muted-foreground">Inactive</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => setRolesUser(u)} data-testid={`user-edit-roles-${u.email}`}>Edit roles</Button>
                    <Button size="icon" variant="ghost" onClick={() => issueReset(u)} title="Issue password reset" data-testid={`user-reset-${u.email}`}>
                      <KeyRound className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => toggleActive(u)} title={u.is_active ? "Deactivate" : "Reactivate"} data-testid={`user-toggle-${u.email}`}>
                      {u.is_active ? <PowerOff className="h-3.5 w-3.5 text-destructive" /> : <Power className="h-3.5 w-3.5 text-[hsl(var(--success))]" />}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {rolesUser && (
          <RolesDialog
            user={rolesUser}
            onClose={() => { setRolesUser(null); queryClient.invalidateQueries({ queryKey: ["admin-users"] }); }}
          />
        )}
        {resetResult && (
          <ResetTokenDialog data={resetResult} onClose={() => setResetResult(null)} />
        )}
      </div>
    </AppShell>
  );
}

function RolesDialog({ user, onClose }) {
  const [roles, setRoles] = useState(new Set(user.roles));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const tplQuery = useQuery({ queryKey: ["role-templates"], queryFn: () => RoleTemplatesAPI.list() });
  const systemKeys = new Set((tplQuery.data || []).filter((t) => t.is_system).map((t) => t.key));
  const builtIn = ALL_ROLES.filter((r) => systemKeys.size === 0 || systemKeys.has(r));
  const customRoles = (tplQuery.data || []).filter((t) => !t.is_system);

  const toggle = (r) => {
    const n = new Set(roles);
    if (n.has(r)) n.delete(r); else n.add(r);
    setRoles(n);
  };
  const save = async () => {
    if (roles.size === 0) { setError("At least one role required"); return; }
    setBusy(true); setError("");
    try {
      await UsersAdminAPI.updateRoles(user.id, Array.from(roles));
      toast.success("Roles updated");
      onClose();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="roles-dialog">
        <DialogHeader>
          <DialogTitle>Edit roles — {user.name}</DialogTitle>
          <DialogDescription className="font-mono text-xs">{user.email}</DialogDescription>
        </DialogHeader>

        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Built-in roles</div>
          <div className="grid grid-cols-2 gap-2">
            {builtIn.map((r) => {
              const tpl = (tplQuery.data || []).find((t) => t.key === r);
              return (
                <label key={r} className="flex items-start gap-2 border border-border rounded-md px-3 py-2 cursor-pointer hover:bg-secondary/60">
                  <Checkbox checked={roles.has(r)} onCheckedChange={() => toggle(r)} data-testid={`roles-dialog-toggle-${r}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium capitalize">{r.replace("_", " ")}</div>
                    {tpl && <div className="text-[10px] text-muted-foreground font-mono">{tpl.permissions.length} permissions</div>}
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {customRoles.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2 mt-1">Custom roles</div>
            <div className="grid grid-cols-2 gap-2">
              {customRoles.map((tpl) => (
                <label key={tpl.key} className="flex items-start gap-2 border border-border rounded-md px-3 py-2 cursor-pointer hover:bg-secondary/60">
                  <Checkbox checked={roles.has(tpl.key)} onCheckedChange={() => toggle(tpl.key)} data-testid={`roles-dialog-toggle-${tpl.key}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{tpl.name}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">{tpl.permissions.length} permissions · {tpl.key}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {error && <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2">{error}</div>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="roles-dialog-save">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetTokenDialog({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => { await navigator.clipboard.writeText(data.reset_token); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="reset-token-dialog">
        <DialogHeader>
          <DialogTitle>Password reset token</DialogTitle>
          <DialogDescription>
            Share with <code className="font-mono text-xs">{data.email}</code>. Expires in {data.expires_in_hours}h.
            (Email delivery ships when SendGrid is wired up.)
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <code className="flex-1 font-mono text-xs bg-secondary px-2 py-1.5 rounded border truncate" data-testid="reset-token-text">{data.reset_token}</code>
          <Button size="sm" variant="outline" onClick={copy}>{copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}</Button>
        </div>
        <DialogFooter><Button onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
